import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridColDef,
  GridColumnHeaderParams,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
  GridValidRowModel,
  useGridApiRef,
  GridPaginationMeta,
  GridAutosizeOptions,
  GridRowParams
} from "@mui/x-data-grid-pro";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DataTableProps } from "./DataTable.props";
import { StyledDataGrid } from "./DataTable.styles";
import { DataTablePagination } from "./DataTablePagination";

export function DataTable<Data extends GridValidRowModel>({
  columns,
  data,
  selectedRows,
  sortingData,
  paginationData,
  totalRows,
  loading,
  tableLocales,
  onSelectRows,
  onSortingChange,
  onPaginationChange,
  isRowSelectable,
  getRowClassName
}: DataTableProps<Data>) {
  const apiRef = useGridApiRef();

  const { t, i18n } = useTranslation();

  const renderHeader = useCallback(
    ({ colDef: { headerName } }: GridColumnHeaderParams) => t(headerName ?? ""),
    [t]
  );

  const autoSizeOptions: GridAutosizeOptions = useMemo(
    () => ({
      columns: columns.map(({ name }) => name),
      includeOutliers: true,
      disableColumnVirtualization: true,
      expand: true
    }),
    [columns]
  );

  useEffect(() => {
    if (!loading && data.length > 0) {
      setTimeout(() => {
        apiRef.current?.autosizeColumns(autoSizeOptions);
      }, 200);
    }
  }, [i18n.language, data, loading]);

  const handleSelectRow = useCallback(
    ({ ids }: GridRowSelectionModel) => {
      onSelectRows(Array.from(ids));
    },
    [onSelectRows]
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      if (model.length > 0) {
        const { field, sort } = model[0];
        onSortingChange({ field, direction: sort });
      } else {
        onSortingChange(undefined);
      }
    },
    [onSortingChange]
  );

  const handlePaginationModelChange = useCallback(
    ({ page, pageSize }: GridPaginationModel) => {
      onPaginationChange({ page, pageSize });
    },
    [onPaginationChange]
  );

  const handleRowSelectableCheck = useCallback(
    (params: GridRowParams<Data>) => {
      if (!isRowSelectable) {
        return true;
      }
      return isRowSelectable(params.row);
    },
    [isRowSelectable]
  );

  const handleRowClassName = useCallback(
    (params: GridRowParams<Data>) => {
      if (!getRowClassName) {
        return "";
      }
      return getRowClassName(params.row);
    },
    [getRowClassName]
  );

  const rowSelectionModel: GridRowSelectionModel = useMemo(
    () => ({
      type: "include",
      ids: new Set(selectedRows)
    }),
    [selectedRows]
  );

  const sortingModel: GridSortModel = useMemo(() => {
    if (!sortingData) {
      return [];
    }
    const { field, direction } = sortingData;
    return [
      {
        field,
        sort: direction
      }
    ];
  }, [sortingData]);

  const paginationModel: GridPaginationModel = useMemo(
    () => ({
      page: paginationData.page,
      pageSize: paginationData.pageSize
    }),
    [paginationData]
  );

  const paginationMeta: GridPaginationMeta = useMemo(() => {
    const { pageSize, page } = paginationModel;

    if (loading) {
      return {
        hasNextPage: false
      };
    }

    return {
      hasNextPage: totalRows > pageSize * page
    };
  }, [totalRows, paginationModel, loading]);

  const columnDefinitions: GridColDef[] = useMemo(
    () =>
      columns.map(({ name, translationKey, valueFormatter }) => ({
        field: name,
        headerName: translationKey,
        renderHeader,
        valueFormatter
      })),
    [columns, renderHeader]
  );

  return (
    <StyledDataGrid
      localeText={{
        noRowsLabel: t(tableLocales.noRowsLocalizationKey ?? ""),
        paginationRowsPerPage: t(tableLocales.pageSizeOptionsLabel ?? "")
      }}
      sx={{ minHeight: "500px" }}
      disableColumnResize
      disableColumnMenu
      disableColumnReorder
      disableVirtualization
      pagination
      paginationMode="server"
      sortingMode="server"
      rowCount={totalRows}
      autosizeOnMount
      paginationMeta={paginationMeta}
      paginationModel={paginationModel}
      onPaginationModelChange={handlePaginationModelChange}
      sortModel={sortingModel}
      onSortModelChange={handleSortModelChange}
      apiRef={apiRef}
      columns={columnDefinitions}
      rows={data}
      autosizeOptions={autoSizeOptions}
      slotProps={{
        loadingOverlay: {
          variant: "linear-progress",
          noRowsVariant: "skeleton"
        }
      }}
      slots={{
        pagination: DataTablePagination
      }}
      initialState={{
        pinnedColumns: {
          left: [GRID_CHECKBOX_SELECTION_COL_DEF.field]
        }
      }}
      onRowSelectionModelChange={handleSelectRow}
      rowSelectionModel={rowSelectionModel}
      loading={loading}
      checkboxSelection={rowSelectionModel.ids.size > 0}
      isRowSelectable={handleRowSelectableCheck}
      getRowClassName={handleRowClassName}
    />
  );
}
